import { type fabric } from 'fabric';
import type Wireframe from '../objects/Wireframe';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import ShapeBase from '../shapes/ShapeBase';
import { isEqual, last, sumBy } from 'lodash';
import { svgToIcon } from '../utils/svg';
import wireframeButton from '../assets/icons/wireframeButton.svg';
import { type ToolList } from '../fabric/ObjectBehaviors';
import { type RecordId } from '../shared-types';
import wireframes from '../wireframes';
import WireframeItem, { isWireframeItem } from './WireframeItem';
import { type RenderedDimensions } from '@lib/canvasx/components/FlexRow';
import { SPACER_3, SPACER_5 } from '../utils/style';
import ItemCollection from '../models/ItemCollection';

const DEFAULT_WIDTH = 100;
const DEFAULT_HEIGHT = 33;
export const PADDING_VERTICAL = 13;
export const PADDING_HORIZONTAL_SQUARE = SPACER_3;
export const PADDING_HORIZONTAL_ROUND = SPACER_5;
export const BORDER_WIDTH = 2;
export const DEFAULT_FONT_SIZE = 14;
export const LABEL_KEY = 'label';
export const PLACEHOLDER_KEY = 'placeholder';

const VERTICAL_COMPONENTS = [
  'navMenuVertical',
  'inputField',
  'dropdown',
  'datePicker',
  'searchBar',
];

export interface ComponentStyles {
  [key: string]: string;
}

export interface ComponentStates {
  [key: string]: string;
}

// Use icon string names to simplify this type, since SvgIcon can derive iconData
// from an icon name
export interface DefaultIcon {
  iconLeft?: string;
  iconRight?: string;
}
class WireframeBase {
  fabricObject: Wireframe;

  updateDimensions: boolean;
  maxItemDimensions: DimensionStruct;
  // Check if setDimension property should be updated based on item width
  checkSetDimension: boolean;

  // Key of selected CanvasX component
  activeKey: InteractiveKey;
  // Flag to ensure activeKey is only on second click
  selected: boolean;

  // Flag to simplify logic for updating drag controls
  hasDraggableItems = false;

  wireframeConstructor: typeof WireframeBase;

  constructor(fabricObject: Wireframe) {
    this.fabricObject = fabricObject;
    this.activeKey = '';
    this.fabricObject.textEditing = false;
    this.updateDimensions = true;
    this.fabricObject.requiresPriorSelection = true;
    this.resetMaxItemDimensions();
    this.initItemCollection();

    // Font size was originally 10 for all components. Since we don't include
    // the font size tool, we have to update the font size in code. If we add
    // the font size tool, we should remove this line.
    this.fabricObject.set('fontSize', 14);

    this.fabricObject.on('moving', () => {
      this.clearActiveItem();
    });

    this.fabricObject.on('mousedown', ev => {
      if (
        this.fabricObject.requiresPriorSelection &&
        !this.fabricObject.selected
      ) {
        return;
      }
      const key = this.fabricObject.findKeyForEvent(ev);
      if ((key && key !== this.activeKey) || (this.activeKey && !key)) {
        this.activeKey = key ?? '';
        this.fabricObject.canvas.requestRenderAll();
        this.fabricObject.controller.actionRerenderToolbar();
        this.fabricObject.fire('modified');
      }
    });

    this.fabricObject.on('deselected', () => (this.activeKey = ''));

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const WireframeClass = wireframes.wireframeNameToClass(
      this.fabricObject.wireframeName
    );
    this.wireframeConstructor = this.constructor as typeof WireframeClass;
  }

  static defaultWidth() {
    return DEFAULT_WIDTH;
  }

  static defaultHeight() {
    return DEFAULT_HEIGHT;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static svgComponent(object: Wireframe) {}

  static externalName() {
    return 'Component';
  }

  static wireframeControls = this.createDefaultControls();
  static createDefaultControls(): fabric.Controls {
    const controls = {
      tl: ShapeBase.resizeControl({ x: -0.5, y: -0.5 }),
      tr: ShapeBase.resizeControl({ x: 0.5, y: -0.5 }),
      bl: ShapeBase.resizeControl({ x: -0.5, y: 0.5 }),
      br: ShapeBase.resizeControl({ x: 0.5, y: 0.5 }),
    };

    return controls;
  }

  static iconSvg() {
    return wireframeButton;
  }

  static renderIcon() {
    return svgToIcon(this.iconSvg());
  }

  static iconSvgStandard() {
    return '';
  }

  static renderStandardIcon() {
    return svgToIcon(this.iconSvgStandard());
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  minDimension({ resizingWidth }: { resizingWidth: boolean }): number {
    console.error('Implement minDimension in subclass');
    return 0;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  maxDimension({ resizingWidth }: { resizingWidth: boolean }): number {
    return Number.MAX_SAFE_INTEGER;
  }

  // Whether the items in this component are in a row or column. Only NavMenu
  // has the layout field serialized, so all other components have to specify
  // this here
  get componentLayout() {
    // Indicates if the acted upon item is in a row
    return 'row';
  }

  // Offset for items inside components, for e.g. dropdown, which has a label
  // and placeholder before the list of items
  componentOffset() {
    return { x: 0, y: 0 };
  }

  // Check to see if a particular style flag is enabled. In almost all cases,
  // flags are enabled, but certain components (like pagination) have special
  // conditions to enable a given flag
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  styleFlagEnabled(key) {
    return true;
  }

  get componentStyle() {
    return this.fabricObject.style ?? 'round';
  }

  get wireframeName() {
    return this.fabricObject.wireframeName;
  }

  get filled() {
    return this.componentStyle.toLowerCase().includes('filled');
  }

  get round() {
    return this.componentStyle.toLowerCase().includes('round');
  }

  get componentState() {
    return this.fabricObject.componentState ?? '';
  }

  // Title to display in tool popup menu
  get componentStatesTitle() {
    return 'State';
  }

  get showComponentStatesTooltips() {
    return false;
  }

  get childrenWidth() {
    if (
      VERTICAL_COMPONENTS.includes(this.wireframeName) ||
      this.fabricObject.items.items.length === 0
    ) {
      return this.maxItemDimensions.width;
    }
    return sumBy(
      this.fabricObject.items.items,
      (item: WireframeItem) => item.dimensions?.unwrappedWidth ?? 0
    );
  }

  get uiStates() {
    return ['default', 'disabled'];
  }

  get itemBorderRadius() {
    return 0;
  }

  // For components that use separators, we need to account for the size of the
  // separator separately, since it is not part of the item's dimensions
  get itemSeparatorSize() {
    return 0;
  }

  initItemCollection() {
    this.fabricObject.items = new ItemCollection(
      this.fabricObject,
      'items',
      WireframeItem
    );
    this.initItems();
  }

  initItems() {}

  tools(): ToolList {
    return [];
  }

  clearActiveItem() {
    this.activeKey = '';
  }

  // Styling of component, e.g., round, filled, etc.
  componentStyles(): ComponentStyles {
    return {};
  }

  // State of component, e.g., on/off, checked/empty, etc.
  componentStates(): ComponentStates {
    return {};
  }

  // State of a given item in a component, e.g., the items within a nav menu
  itemStates() {
    return ['default', 'selected'];
  }

  activeItemState() {
    return this.activeItem()?.state ?? 'default';
  }

  setItemState(state: string) {
    // Only allow one selected state at a time
    if (state === 'selected') {
      this.fabricObject.items.items.forEach(item => {
        if (item.state === 'selected') {
          this.fabricObject.items.update(item, { state: 'default' });
        }
      });
    }

    const item = this.activeItem();
    if (isWireframeItem(item)) {
      this.fabricObject.items.update(item, { state });
      this.updateDimensions = true;
      this.fabricObject.canvas.requestRenderAll();
    }
  }

  addPlaceholder(placeholder: Partial<Wireframe['placeholder']>) {
    const { iconLeft, iconRight, value, state } = placeholder;
    this.fabricObject.set('placeholder', { iconLeft, iconRight, value, state });
  }

  setPlaceholderState(state: string) {
    if (!this.placeholder?.value) {
      this.fabricObject.set(['placeholder', 'value'], 'Text');
    }
    this.fabricObject.set(['placeholder', 'state'], state);
    this.checkSetDimension = true;
    this.updateDimensions = true;
    this.fabricObject.canvas.requestRenderAll();
    this.fabricObject.controller.undoManager.completeUndoGroup();
  }

  setPlaceholderValue(value: string) {
    this.fabricObject.set(['placeholder', 'value'], value);
    this.checkSetDimension = true;
    this.updateDimensions = true;
    this.fabricObject.canvas.requestRenderAll();
    this.fabricObject.controller.undoManager.completeUndoGroup();
  }

  get placeholder() {
    return this.fabricObject.placeholder;
  }

  static labelPositions() {
    return ['inside', 'outside', 'noLabel'];
  }

  labelPositions() {
    return WireframeBase.labelPositions();
  }

  labelPosition() {
    return this.label?.position ?? 'noLabel';
  }

  addLabel(label: Wireframe['label']) {
    this.fabricObject.set('label', {
      value: label.value,
      position: label.position,
    });
  }

  setLabelPosition(position: string) {
    // Backwards compatibility for components without labels
    if (!this.label?.value) {
      this.fabricObject.set(['label', 'value'], 'Label');
    }

    this.fabricObject.set(['label', 'position'], position);
    this.checkSetDimension = true;
    this.updateDimensions = true;
    this.fabricObject.canvas.requestRenderAll();
    this.fabricObject.controller.undoManager.completeUndoGroup();
  }

  setLabelValue(value) {
    this.fabricObject.set(['label', 'value'], value);
    this.checkSetDimension = true;
    this.updateDimensions = true;
    this.fabricObject.canvas.requestRenderAll();
    this.fabricObject.controller.undoManager.completeUndoGroup();
  }

  get label() {
    return this.fabricObject.label;
  }

  get showLabel(): boolean {
    return Boolean(this.label?.position) && this.label.position !== 'noLabel';
  }

  // Special handling for updating placeholder text for inputField component
  togglePlaceholderState() {
    console.error('implement in subclass');
  }

  itemStateWasSet(keyOrPath: KeyPath, value: any) {
    if (Array.isArray(keyOrPath) && keyOrPath.length === 3) {
      const { items } = this.fabricObject;
      const id = keyOrPath[1] as RecordId;
      const item = items?.findById(id);
      if (item && item.state !== value) {
        items.update(item, { state: value });
      }
    }
  }

  defaultText() {
    return 'Item';
  }

  defaultIcon(): DefaultIcon {
    return {};
  }

  // RecordId type requires that we verify a number within a string
  toRecordId(value: string | number): RecordId | undefined {
    const numString = String(value);
    if (!isNaN(Number(numString))) {
      return numString as RecordId;
    }
    return undefined;
  }

  activeItem(): Wireframe['placeholder'] | WireframeItem | undefined {
    // See if we have an item selected
    const { items } = this.fabricObject;
    if (items) {
      const itemId = this.activeItemId();
      if (itemId) {
        return items.findById(itemId);
      }
    }

    // If there is a placeholder, assume we are using that
    return this.placeholder;
  }

  activeItemId(): RecordId | undefined {
    if (this.activeKey) {
      const idString = last(this.activeKey.split(':'));
      return this.toRecordId(idString!);
    }
    return undefined;
  }

  guardActiveKey() {
    if (!this.activeKey) {
      const selection = this.fabricObject.controller.selection;
      // Only check wireframe objects
      if (selection.type !== 'wireframe') return;

      // For wireframes with a single item, the user will see tools for editing
      // that item without having to click on that item first (e.g., when first
      // creating a wireframe of that type)
      const items = selection.firstObject().items?.items;
      this.activeKey = items?.length === 1 ? items[0].id : '';
      if (!this.activeKey) {
        throw new Error('Unable to find item for invalid key');
      }
    }
  }

  addItem() {
    if (!this.fabricObject.items) {
      throw new Error('Object must have items to add');
    }

    const icon = this.defaultIcon();

    this.fabricObject.items.add({
      value: this.defaultText(),
      ...icon,
    });
    this.updateDimensions = true;
    this.fabricObject.canvas.requestRenderAll();
  }

  removeLastItem() {
    if (!this.fabricObject.items) {
      throw new Error('Object must have items to add');
    }

    this.fabricObject.items.removeLast();
    this.updateDimensions = true;
    this.fabricObject.canvas.requestRenderAll();
  }

  toolRemoveItemEnabled() {
    return true;
  }

  deleteItem(id?: RecordId) {
    if (!this.fabricObject.items) {
      throw new Error('Object must have items to delete');
    }

    id ||= this.activeItemId();

    if (!id) {
      console.error('Invalid object id for delete');
      return;
    }

    this.fabricObject.items.remove(id);
    this.activeKey = '';
    this.updateDimensions = true;
    this.fabricObject.canvas?.requestRenderAll();
  }

  updateActiveItem(itemState) {
    const item = this.activeItem();
    if (item) {
      if (isWireframeItem(item)) {
        this.fabricObject.items.update(item, itemState);
      } else {
        // we know at this point that we are dealing with a placeholder
        this.fabricObject.set('placeholder', { ...item, ...itemState });
      }
      this.checkSetDimension = true;
      this.updateDimensions = true;
      this.fabricObject.canvas.requestRenderAll();
      this.fabricObject.controller.undoManager.completeUndoGroup();
    }
  }

  onItemChange(value) {
    this.updateActiveItem({ value });
  }

  // Tracks dimensions for children. Height and width may come from different
  // children
  updateMaxItemDimensions({
    dimensions,
    useUnwrappedWidth = false,
  }: {
    dimensions: RenderedDimensions;
    useUnwrappedWidth: boolean;
  }) {
    const { unwrappedWidth, width, height } = dimensions;
    const itemWidth =
      useUnwrappedWidth && unwrappedWidth ? unwrappedWidth : width;

    if (itemWidth > this.maxItemDimensions.width) {
      this.maxItemDimensions.width = itemWidth;
    }
    if (height > this.maxItemDimensions.height) {
      this.maxItemDimensions.height = height;
    }
  }

  resetMaxItemDimensions() {
    this.maxItemDimensions = { width: 0, height: 0 };
  }

  supportedIcons() {
    return ['iconLeft', 'iconRight'];
  }

  itemIcon(location) {
    return this.activeItem()?.[location];
  }

  setIcon(location, icon) {
    // Only allow one icon per item
    const otherLocation = location.toLowerCase().includes('left')
      ? 'iconRight'
      : 'iconLeft';
    this.updateActiveItem({ [location]: icon, [otherLocation]: null });
    this.fabricObject.canvas.requestRenderAll();
  }

  wasSet(keyOrPath: KeyPath, value: any) {
    if (keyOrPath === 'style') {
      this.updateDimensions = true;
      this.fabricObject.canvas?.requestRenderAll();
      this.fabricObject.fire('modified');
    }

    // Handle undo case
    if (Array.isArray(keyOrPath) && keyOrPath.includes('itemsData')) {
      this.updateDimensions = true;
      if (keyOrPath.includes('state')) {
        this.itemStateWasSet(keyOrPath, value);
      }
    }

    if (keyOrPath.includes('flag')) {
      this.updateDimensions = true;
    }

    if (keyOrPath === 'fontSize') {
      this.updateDimensions = true;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  render(ctx: CanvasRenderingContext2D) {
    throw new Error('Implement render() to render the wireframe');
  }

  // Allow component to autosize if items exceed size of dimensions set
  // by user
  _checkSetDimension() {
    const { width, setDimension } = this.fabricObject;

    if (setDimension && this.checkSetDimension) {
      this.fabricObject.set('setDimension', this.childrenWidth <= width);
    }

    this.checkSetDimension = false;
  }

  _updateRenderedDimensions(dimensions: DimensionStruct) {
    const { width, height } = this.fabricObject;

    this._checkSetDimension();

    this.updateDimensions ||= !isEqual(
      { width: dimensions.width, height: dimensions.height },
      { width, height }
    );
    if (this.updateDimensions) {
      this.updateDimensions = false;
      this.fabricObject.withoutCollaboration(() => {
        this.fabricObject.set({
          width: dimensions.width,
          height: dimensions.height,
        });
      });
      this.fabricObject.canvas.requestRenderAll();
      this.fabricObject.setCoords();
    }
  }
}

export default WireframeBase;
